import { createSeoBuilder } from '@platform/seo';
import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  SITE_URL,
  TITLE_TEMPLATE,
} from './site.constant';

/**
 * 사이트 공통 SEO 기본값. 페이지별 메타는 이 빌더로 만든다.
 *
 * 사용 예 (페이지 컴포넌트 또는 SeoRoute config):
 *
 *   import { buildSeo } from '@/shared/seo/seoBuilder';
 *
 *   const seo = buildSeo({
 *     title: 'XCOST PRO',
 *     description: '...',
 *     path: '/products/xcost-pro',
 *     og: { image: '/og/xcost-pro.png', type: 'product' },
 *   });
 *
 *   return <SeoRoute config={seo}><Page /></SeoRoute>;
 *
 * ── canonical 호스트 ─────────────────────────────────────────────
 * `www.howcost.co.kr` **한 곳으로 고정**한다. canonical 은 "이 내용의 정본이 어디냐"를
 * 말하는 태그라 환경마다 달라지면 안 된다 — dev/staging 이 크롤링돼도 운영으로
 * 합쳐지는 게 맞는 동작이다.
 *
 * ⚠️ 예전 기본값이 `www.howcost.com` 이었고 어느 env 에도 VITE_SITE_URL 이 없어서,
 *    전 페이지 canonical 이 쓰지 않는 도메인을 가리키고 있었다(= 검색엔진에
 *    "정본은 저기"라고 알리는 상태). 값은 site.constant.ts 한 곳에서만 바꾼다.
 *
 * VITE_SITE_URL 은 프리뷰 환경에서 일시적으로 덮어쓸 때만 쓴다.
 * console-web / admin-web 은 @platform/seo 를 쓰지 않아 canonical 자체가 없다(의도).
 */
export const buildSeo = createSeoBuilder({
  siteName: SITE_NAME,
  titleTemplate: TITLE_TEMPLATE,
  baseUrl: import.meta.env.VITE_SITE_URL ?? SITE_URL,
  defaultDescription:
    '건설의 모든 비용, 한 줄로 정렬되다. CAD·BIM 도면에서 적산까지. 1만 명의 건설인이 신뢰하는 통합 견적 솔루션.',
  defaultOgImage: DEFAULT_OG_IMAGE,
  defaultLocale: 'ko_KR',
  // seo.plugin.ts 와 같은 기준: production(www) 빌드만 색인.
  indexable: import.meta.env.MODE === 'production',
});

/** 자주 쓰는 SEO 부분 객체 (예: 제품 카테고리 공통) */
export const PRODUCT_SEO_BASE = {
  og: { type: 'product' as const },
  keywords: [
    '건설적산',
    '건축적산',
    '내역작성',
    '표준품셈',
    '수량산출',
    'CAD 적산',
    'BIM 적산',
    'XCOST',
    'XPIXELS',
    'HOWCOST',
    '하우코스트',
  ],
} as const;
